import * as React from 'react';
import { useRef } from 'react';
import type { CSSProperties } from 'react';
import { TooltipProvider } from '@ooh/ui/components/tooltip';
import { Table, TableBody, TableCell, TableRow } from '@ooh/ui/components/table';
import type {
  AvailabilityGridData,
  AvailabilityGridRow,
  AvailabilityPeriod,
  CellState,
  CellSelection,
  EntriesGridData,
  MultiCellSelection,
  MultiSelectCellRef,
} from '../lib/availability-types';
import type { CellPopoverLabels } from './cell-popover-state';
import { AvailabilityCellsRow } from './availability-cells-row';
import { computeMultiSelectRectangle, isShiftSelectableState } from './availability-grid-selection';
import { AvailabilityGridHead, AvailabilityRowHeaderCell } from './availability-grid-head';

export interface AvailabilityGridProps {
  rows: AvailabilityGridRow[];
  periods: AvailabilityPeriod[];
  grid: AvailabilityGridData;
  /**
   * Optional multi-stack entries per cell.
   * When provided for a cell, renders MultiStackCellContent instead of the single-icon fallback.
   * Backward-compatible: callers that don't need multi-stack omit this prop.
   */
  entriesGrid?: EntriesGridData;
  selection?: CellSelection | null;
  leftHeaderLabel: string;
  showRowHeader?: boolean;
  rangeStartLabel?: string;
  rangeEndLabel?: string;
  colWidth?: number;
  popoverLabels: CellPopoverLabels;
  soldAriaLabel: string;
  /** Accessible label for the OPTION-bucket status icon fallback (no customContent/entries). */
  optionAriaLabel?: string;
  /** Accessible label for the merged reserved-bucket status icon fallback (no customContent/entries). */
  reservedAriaLabel?: string;
  /**
   * When true, the grid never renders its internal CellPopover even when a cell is selected.
   * Cell highlighting via selection / isSelected still works.
   * Use on inventory and detail surfaces where the route renders its own external CellPopover.
   * Defaults to false — offer-draft tab relies on the grid-internal popover.
   */
  disableInternalPopover?: boolean;
  /**
   * When true, SOLD cells are treated as clickable so they can open a read-only
   * details popover. Defaults to false (offer-availability view: SOLD is final, no actions).
   */
  soldCellClickable?: boolean;
  getReservationStatusForPeriod?: (
    unitId: string,
    periodId: string,
  ) => 'SOFT_RESERVATION' | 'HARD_RESERVATION' | null;
  getOwnCellTtlExpiresAt?: (unitId: string, periodId: string) => Date | null | undefined;
  /** Called once when any visible TTL countdown reaches zero — use to trigger a data refetch. */
  onTtlExpired?: () => void;
  selectedCellClassName?: string;
  selectedCellStyle?: CSSProperties;
  onCellClick?: (unitId: string, period: AvailabilityPeriod, shiftKey: boolean) => void;
  onUnitClick?: (unitId: string) => void;
  onClosePopover?: () => void;
  onReserve?: (selection: CellSelection, status: 'SOFT_RESERVATION' | 'OPTION') => void;
  onUpgradeHard?: (selection: CellSelection) => void;
  onCloseSale?: (selection: CellSelection) => void;
  onRelease?: (selection: CellSelection) => void;
  /**
   * Returns queue info for a given cell, or null when no queue data is available.
   * myPosition === null means the current user is not in the queue.
   */
  getCellQueueInfo?: (
    unitId: string,
    periodId: string,
  ) => { count: number; myPosition: number | null } | null;
  /** Called when the current user joins the waitlist queue for a cell. */
  onJoinQueue?: (selection: CellSelection) => void;
  /** Called when the current user leaves the waitlist queue for a cell. */
  onLeaveQueue?: (selection: CellSelection) => void;
  /** Drag-range selection: called on mousedown to start a drag. */
  onCellMouseDown?: (unitId: string, period: AvailabilityPeriod) => void;
  /** Drag-range selection: called on mouseenter while drag is active. */
  onCellMouseEnter?: (unitId: string, period: AvailabilityPeriod) => void;
  /** Drag-range selection: called on mouseup to commit. */
  onCellMouseUp?: (unitId: string, period: AvailabilityPeriod) => void;
  renderCellContent?: (
    row: AvailabilityGridRow,
    period: AvailabilityPeriod,
    cellState: CellState,
    colWidth: number,
  ) => React.ReactNode;
  containerRef?: React.Ref<HTMLDivElement>;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  /**
   * Cursor-based infinite-scroll wiring (server-side pagination). The sentinel
   * element is rendered after the table, inside this component's own scroll
   * container, only while `hasNextPage` is true. Optional — callers that don't
   * paginate (e.g. read-only/inventory surfaces) omit these props.
   */
  sentinelRef?: React.Ref<HTMLDivElement>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  loadingMoreLabel?: string;
  /**
   * Additive opt-in for rectangular multi-cell selection (click / drag / shift-click)
   * spanning any set of rows (units) and columns (periods). A drag/shift-click rectangle
   * is filtered to MULTI-SELECT-ELIGIBLE cell states (`free` | `option-own` |
   * `reserved-own` | `option-other` | `reserved-other`); a lone (non-shift) click still
   * only starts a selection on the narrower SELECT-eligible set (`free` | `option-own` |
   * `reserved-own`) — see `isMultiSelectEligibleState`/`isShiftSelectableState`. Defaults to `false`, leaving the
   * existing single-cell `selection`/`onCellClick`/`onCellMouseDown`/`onCellMouseEnter`/
   * `onCellMouseUp` behavior byte-for-byte unchanged. When `true`, those four props are
   * ignored — the grid manages click/drag/shift-click internally and reports the result
   * via `onMultiSelectionChange`. See `unified-availability-interaction` spec, "Multi-select
   * opt-in on the shared availability grid".
   */
  multiSelect?: boolean;
  /**
   * Controlled multi-cell selection state. Only meaningful when `multiSelect` is true —
   * its `anchor` is reused as the shift-click fill anchor for the next gesture, so callers
   * MUST feed back whatever `onMultiSelectionChange` last reported (mirroring the existing
   * `selection` controlled-prop pattern).
   */
  multiSelection?: MultiCellSelection | null;
  /** Called whenever a click / drag / shift-click gesture changes the multi-cell selection. Only fires when `multiSelect` is true. */
  onMultiSelectionChange?: (selection: MultiCellSelection | null) => void;
}

// eslint-disable-next-line complexity -- soldCellClickable prop added one branch; extraction would split a cohesive layout function.
export function AvailabilityGrid({
  rows,
  periods,
  grid,
  entriesGrid,
  selection = null,
  leftHeaderLabel,
  showRowHeader = true,
  rangeStartLabel,
  rangeEndLabel,
  colWidth = 80,
  popoverLabels,
  soldAriaLabel,
  optionAriaLabel,
  reservedAriaLabel,
  disableInternalPopover = false,
  soldCellClickable = false,
  getReservationStatusForPeriod,
  getOwnCellTtlExpiresAt,
  onTtlExpired,
  selectedCellClassName,
  selectedCellStyle,
  onCellClick,
  onUnitClick,
  onClosePopover,
  onReserve,
  onUpgradeHard,
  onCloseSale,
  onRelease,
  getCellQueueInfo,
  onJoinQueue,
  onLeaveQueue,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  renderCellContent,
  containerRef,
  onScroll,
  sentinelRef,
  hasNextPage = false,
  isFetchingNextPage = false,
  loadingMoreLabel,
  multiSelect = false,
  multiSelection = null,
  onMultiSelectionChange,
}: AvailabilityGridProps) {
  const hasRangeStart = rangeStartLabel !== undefined;
  const hasRangeEnd = rangeEndLabel !== undefined;
  const edgeColWidth = Math.max(colWidth, 160);
  // Deterministic pixel total mirroring the same columns built into the <colgroup>
  // below — see the width-sizing comment on <Table> for why this replaces the old
  // w-auto/minWidth:max-content approach.
  const totalTableWidth =
    (showRowHeader ? 180 : 0) +
    (hasRangeStart ? edgeColWidth : 0) +
    (hasRangeEnd ? edgeColWidth : 0) +
    colWidth * periods.length;

  // Transient in-progress-drag tracking only — the actual selection value stays
  // controlled via `multiSelection`/`onMultiSelectionChange`, mirroring the existing
  // `selection` controlled-prop pattern (see `unified-availability-interaction` spec).
  const dragStartRef = useRef<MultiSelectCellRef | null>(null);
  const isDraggingRef = useRef(false);

  function handleMultiCellClick(unitId: string, period: AvailabilityPeriod, shiftKey: boolean) {
    if (shiftKey && multiSelection?.anchor) {
      const cells = computeMultiSelectRectangle(rows, periods, grid, multiSelection.anchor, {
        unitId,
        periodId: period.id,
      });
      onMultiSelectionChange?.({ cells, anchor: { unitId, periodId: period.id } });
      return;
    }
    const cellState = grid[unitId]?.[period.id] ?? 'free';
    if (!isShiftSelectableState(cellState)) return;
    onMultiSelectionChange?.({
      cells: [{ unitId, periodId: period.id }],
      anchor: { unitId, periodId: period.id },
    });
  }

  function handleMultiCellMouseDown(unitId: string, period: AvailabilityPeriod) {
    dragStartRef.current = { unitId, periodId: period.id };
    isDraggingRef.current = false;
  }

  function handleMultiCellMouseEnter(unitId: string, period: AvailabilityPeriod) {
    const start = dragStartRef.current;
    if (!start || (start.unitId === unitId && start.periodId === period.id)) return;
    isDraggingRef.current = true;
    const cells = computeMultiSelectRectangle(rows, periods, grid, start, {
      unitId,
      periodId: period.id,
    });
    onMultiSelectionChange?.({ cells, anchor: { unitId, periodId: period.id } });
  }

  function handleMultiCellMouseUp(unitId: string, period: AvailabilityPeriod) {
    const start = dragStartRef.current;
    if (isDraggingRef.current && start) {
      const cells = computeMultiSelectRectangle(rows, periods, grid, start, {
        unitId,
        periodId: period.id,
      });
      onMultiSelectionChange?.({ cells, anchor: { unitId, periodId: period.id } });
    }
    dragStartRef.current = null;
    isDraggingRef.current = false;
  }

  const effectiveOnCellClick = multiSelect ? handleMultiCellClick : onCellClick;
  const effectiveOnCellMouseDown = multiSelect ? handleMultiCellMouseDown : onCellMouseDown;
  const effectiveOnCellMouseEnter = multiSelect ? handleMultiCellMouseEnter : onCellMouseEnter;
  const effectiveOnCellMouseUp = multiSelect ? handleMultiCellMouseUp : onCellMouseUp;

  // Wrapped in a `TooltipProvider` (rather than requiring every AvailabilityGrid caller to
  // supply its own ancestor one) so `AvailabilityCellVisual`'s owner-hint tooltip works on
  // every surface that renders through this grid (DetailAvailabilityCard, offer-draft
  // Availability tab) — the inventory catalog's RichAvailabilityCell path renders outside
  // this grid and keeps its own route-level TooltipProvider.
  const gridContent = (
    <div
      className="overflow-x-auto overflow-y-auto flex-1"
      data-testid="availability-grid-container"
      ref={containerRef}
      onScroll={onScroll}
    >
      {/* Explicit pixel `width` (totalTableWidth, computed above from the same row-header +
          edge + colWidth*periods.length values as the <colgroup> below) overrides the shared
          Table's default w-full (packages/ui/src/components/table.tsx), mirroring how
          packages/ui's DataTable sizes itself when every column has an explicit width
          (computeTableWidthInfo's allColsHaveWidths branch). This still avoids the original
          CU-86caypxpc bug — a 100%-wide table with table-layout: fixed proportionally
          redistributing leftover space across every <col>, blowing up the fixed 180px
          row-header column whenever period columns undershoot the container — because the
          table no longer needs to guess an auto/percentage layout at all; the total width is
          already exact. It also fixes a second bug the old `minWidth: 'max-content'` approach
          caused: that forces the table's own box to grow to fit its *unwrapped* content
          (e.g. a long owner-hint span), defeating the <col> pixel widths and the `truncate`
          classes inside cells — the offer-draft tab ballooned far past `colWidth` regardless
          of what was passed in. An explicit `width` can't be blown up by content. */}
      <Table className="border-collapse" style={{ tableLayout: 'fixed', width: totalTableWidth }}>
        <AvailabilityGridHead
          periods={periods}
          showRowHeader={showRowHeader}
          leftHeaderLabel={leftHeaderLabel}
          rangeStartLabel={rangeStartLabel}
          rangeEndLabel={rangeEndLabel}
          colWidth={colWidth}
          edgeColWidth={edgeColWidth}
        />
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} className="group" data-testid={`availability-row-${row.id}`}>
              {showRowHeader ? (
                <AvailabilityRowHeaderCell row={row} onUnitClick={onUnitClick} />
              ) : null}
              {hasRangeStart ? (
                <TableCell
                  className="h-10 border border-dashed border-border bg-background/80 px-2 py-0"
                  aria-hidden="true"
                />
              ) : null}
              <AvailabilityCellsRow
                row={row}
                periods={periods}
                grid={grid}
                entriesGrid={entriesGrid}
                colWidth={colWidth}
                selection={selection}
                popoverLabels={popoverLabels}
                soldAriaLabel={soldAriaLabel}
                optionAriaLabel={optionAriaLabel}
                reservedAriaLabel={reservedAriaLabel}
                disableInternalPopover={disableInternalPopover}
                soldCellClickable={soldCellClickable}
                getReservationStatusForPeriod={getReservationStatusForPeriod}
                getOwnCellTtlExpiresAt={getOwnCellTtlExpiresAt}
                onTtlExpired={onTtlExpired}
                selectedCellClassName={selectedCellClassName}
                selectedCellStyle={selectedCellStyle}
                onCellClick={effectiveOnCellClick}
                onClosePopover={onClosePopover}
                onReserve={onReserve}
                onUpgradeHard={onUpgradeHard}
                onCloseSale={onCloseSale}
                onRelease={onRelease}
                getCellQueueInfo={getCellQueueInfo}
                onJoinQueue={onJoinQueue}
                onLeaveQueue={onLeaveQueue}
                onCellMouseDown={effectiveOnCellMouseDown}
                onCellMouseEnter={effectiveOnCellMouseEnter}
                onCellMouseUp={effectiveOnCellMouseUp}
                renderCellContent={renderCellContent}
                multiSelection={multiSelect ? multiSelection : null}
              />
              {hasRangeEnd ? (
                <TableCell
                  className="h-10 border border-dashed border-border bg-background/80 px-2 py-0"
                  aria-hidden="true"
                />
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hasNextPage ? (
        <div
          ref={sentinelRef}
          data-testid="availability-tab-scroll-sentinel"
          aria-hidden="true"
          className="h-px w-full"
        />
      ) : null}
      {isFetchingNextPage ? (
        <div className="p-3 text-center text-xs text-muted-foreground">{loadingMoreLabel}</div>
      ) : null}
    </div>
  );

  return <TooltipProvider>{gridContent}</TooltipProvider>;
}
